import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'

import OrderTooltip from '~/components/dashboard/soap/OrderTooltip'
import FeatureToggle from '~/constants/featureToggle'
import { fetchPrescriptionPrintInfo } from '~/store/actions/orders'
import { getItemErrors } from '~/store/duck/clientFinanceData'
import {
  useIsFood,
  useIsGlobalInventoryItem,
  useOpenPrescriptionDialogAfterFetchUnifiedOrder,
} from '~/store/hooks/orders'
import {
  useDeletePrescription,
  useIsChewyActiveRxReadOnly,
  useIsDeclinableOrDeletable,
} from '~/store/hooks/prescription'
import { getFeatureToggle } from '~/store/reducers/constants'
import { Order } from '~/types'
import { getPrescriptionType } from '~/utils/prescription'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import ItemWithBadgesActions, {
  ItemWithBadgesActionsProps,
} from '../../item-with-badges/ItemWithBadgesActions'

export interface PrescriptionItemActionsLineItemProps
  extends ItemWithBadgesActionsProps {
  isInvoice: boolean
  isLineItem?: boolean
  isRetailOrderLineItem?: boolean
  onDelete?: () => void
  order: Order
}

const PrescriptionItemActionsLineItem = ({
  onDelete: onDeleteProp,
  order,
  isLineItem,
  isInvoice,
  isRetailOrderLineItem,
  ...rest
}: PrescriptionItemActionsLineItemProps) => {
  const dispatch = useDispatch()

  const isGlobalItem = useIsGlobalInventoryItem(order)
  const isFood = useIsFood(order)

  const id = isLineItem ? order.logId : order.id

  const isContextItem = useIsCurrentContextItem(order)

  const { isInHouse, isChewyActiveRx } = isRetailOrderLineItem
    ? { isInHouse: false, isChewyActiveRx: true }
    : getPrescriptionType(order.prescriptionType, order.origin)

  const isEditPostedChargesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.EDIT_POSTED_CHARGES),
  )

  const stateId = isRetailOrderLineItem ? order.logState?.id : order.stateId

  const isChewyReadOnly = useIsChewyActiveRxReadOnly({
    isChewyActiveRx,
    stateId,
    isInvoice,
  })

  const isDeclinableOrDeletable = useIsDeclinableOrDeletable({
    orderType: order.type,
    stateId,
    prescriptionType: order.prescriptionType,
    origin: order.origin,
  })
  const { fetchUnifiedOrderAndOpenPrescription } =
    useOpenPrescriptionDialogAfterFetchUnifiedOrder({
      isChargeSheet: true,
      isInvoice,
    })

  const lineItemErrors = useSelector(getItemErrors)
  const forceDelete =
    lineItemErrors &&
    lineItemErrors.length > 0 &&
    !R.isNil(order) &&
    lineItemErrors.filter((itemError) => itemError.lineItemId === order.id)
      .length > 0

  const handleDelete = useDeletePrescription({
    hasRefills: Boolean(order.refills?.length),
    isFromSoap: Boolean(order.soapId),
    name: order.name,
    retailOrderLineItemLogId: isRetailOrderLineItem
      ? order.logId || undefined
      : undefined,
    order,
    forceDelete,
  })

  const openPrescription = () => {
    if (id) {
      fetchUnifiedOrderAndOpenPrescription(id)
    }
  }

  const handlePrint = () => {
    if (id) {
      dispatch(fetchPrescriptionPrintInfo({ ...order, id }))
    }
  }

  if (isEditPostedChargesEnabled) {
    return (
      <ItemWithBadgesActions
        tooltip={
          isRetailOrderLineItem ? null : (
            <OrderTooltip forceShowPrices order={order} />
          )
        }
        onAddRX={openPrescription}
        onDelete={handleDelete}
        onEdit={openPrescription}
        onPrint={handlePrint}
        onView={openPrescription}
        {...rest}
      />
    )
  }

  return (
    <ItemWithBadgesActions
      tooltip={
        isInHouse ? <OrderTooltip forceShowPrices order={order} /> : undefined
      }
      onAddRX={!isChewyReadOnly && isFood ? openPrescription : undefined}
      onDelete={isDeclinableOrDeletable ? handleDelete : undefined}
      onEdit={!isChewyReadOnly && isGlobalItem ? openPrescription : undefined}
      onPrint={!isChewyActiveRx && isContextItem ? handlePrint : undefined}
      onView={isChewyReadOnly ? openPrescription : undefined}
      {...rest}
    />
  )
}

export default PrescriptionItemActionsLineItem
